import { useQuery } from '@tanstack/react-query';
import { client } from '@xinfer/api-client';
import type { Account } from '@xinfer/api-client';
import { explainKvTokenSeconds, formatInteger } from '../lib/format';

export function Usage({ account }: { account: Account }) {
  const usage = useQuery({
    queryKey: ['usage', account.id],
    queryFn: () => client.usage(account.id),
  });

  function exportCsv() {
    if (!usage.data) return;
    const rows = [
      ['metric', 'value'],
      ['requests', String(usage.data.requests)],
      ['input_tokens', String(usage.data.input_tokens)],
      ['output_tokens', String(usage.data.output_tokens)],
      ['kv_token_seconds', String(usage.data.kv_token_seconds)],
      ['peak_kv_tokens', String(usage.data.peak_kv_tokens)],
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `xinfer-usage-${account.id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <div className="spread">
        <div>
          <h1>Usage</h1>
          <p className="subtitle">Everything the service meters, including what it does not yet charge for.</p>
        </div>
        <button className="ghost" onClick={exportCsv} disabled={!usage.data}>
          Export CSV
        </button>
      </div>

      {usage.isLoading && <p className="muted">Loading usage…</p>}
      {usage.isError && <p className="error">Could not load usage.</p>}

      {usage.data && (
        <table>
          <thead>
            <tr>
              <th>Metric</th>
              <th>Value</th>
              <th>Meaning</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Requests</td>
              <td className="mono">{formatInteger(usage.data.requests)}</td>
              <td className="muted">completed inference calls</td>
            </tr>
            <tr>
              <td>Input tokens</td>
              <td className="mono">{formatInteger(usage.data.input_tokens)}</td>
              <td className="muted">prompt, after prefix-cache reuse</td>
            </tr>
            <tr>
              <td>Output tokens</td>
              <td className="mono">{formatInteger(usage.data.output_tokens)}</td>
              <td className="muted">generated</td>
            </tr>
            <tr>
              <td>KV-token-seconds</td>
              <td className="mono">{formatInteger(usage.data.kv_token_seconds)}</td>
              <td className="muted">{explainKvTokenSeconds(usage.data.kv_token_seconds)}</td>
            </tr>
            <tr>
              <td>Peak KV tokens</td>
              <td className="mono">{formatInteger(usage.data.peak_kv_tokens)}</td>
              <td className="muted">largest context held at once</td>
            </tr>
          </tbody>
        </table>
      )}
    </>
  );
}
